
import { Button, Checkbox, Input } from "@reactive-resume/ui";
import { useEffect, useState, useRef } from "react";

// Simple ID generator
const generateId = () => Math.random().toString(36).substring(2, 9);

type SummaryItem = {
  id: string;
  text: string;
  isChecked: boolean;
};

type Props = {
    value?: string;
    onChange: (value: string) => void;
};

export const MastersUnionListEditor = ({ value, onChange }: Props) => {
  const [items, setItems] = useState<SummaryItem[]>([]);
  const [linkEditorItemId, setLinkEditorItemId] = useState<string | null>(null);
  const [linkUrl, setLinkUrl] = useState("");
  const editableRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const selectionRanges = useRef<{ [key: string]: Range | null }>({});
  const lastSyncedHtmlRef = useRef<string>("");

  // Initialize from content
  useEffect(() => {
    if (value === lastSyncedHtmlRef.current) return;

    if (value) {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = value;
        
        const children = Array.from(tempDiv.children);
        
        const parsedItems: SummaryItem[] = [];

        if (children.length === 0 && value.trim()) {
             parsedItems.push({
                 id: generateId(),
                 text: value,
                 isChecked: false
             });
        } else {
             const processChild = (child: Element) => {
                if (child.tagName === 'UL' || child.tagName === 'OL') {
                    Array.from(child.children).forEach(processChild);
                    return;
                }

                let text = child.innerHTML || "";
                let isChecked = false;

                const hasBold = child.querySelector("b, strong") || (child as any).style.fontWeight === "bold";
                const hasItalic = child.querySelector("i, em") || (child as any).style.fontStyle === "italic";
                const hasUnderline = child.querySelector("u") || (child as any).style.textDecoration === "underline";

                if (hasBold && hasItalic && hasUnderline && child.tagName === 'P') {
                    isChecked = true;
                    text = child.textContent || "";
                }
                
                parsedItems.push({
                    id: generateId(),
                    text: text,
                    isChecked: isChecked
                });
             };

             children.forEach(processChild);
        }
        
        if (parsedItems.length === 0) {
            parsedItems.push({ id: generateId(), text: "", isChecked: false });
        }

        setItems(parsedItems);
    } else {
        setItems([{ id: generateId(), text: "", isChecked: false }]);
    }
    lastSyncedHtmlRef.current = value ?? "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Update parent when items change
  useEffect(() => {
    if (items.length === 0) return;

    const htmlContent = items.map(item => {
        if (item.isChecked) {
            return `<p style="margin: 0;"><b><i><u>${item.text}</u></i></b></p>`;
        } else {
            return `<ul style="margin: 0; margin-left: 10px !important; padding-left: 0;"><li style="padding-left: 0; margin-left: 0 !important;">${item.text}</li></ul>`;
        }
    }).join("");
    
    if (htmlContent !== value) {
        lastSyncedHtmlRef.current = htmlContent;
        const handler = setTimeout(() => {
            onChange(htmlContent);
        }, 300);
        return () => clearTimeout(handler);
    }
  }, [items, onChange, value]);

  const handleAddItem = () => {
    setItems(prev => [...prev, { id: generateId(), text: "", isChecked: false }]);
  };

  const handleDeleteItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const handleUpdateItem = (id: string, updates: Partial<SummaryItem>) => {
    setItems(prev => prev.map(item => {
        if (item.id !== id) return item;
        return { ...item, ...updates };
    }));
  };

  const applyFormat = (id: string, format: 'bold' | 'italic' | 'underline') => {
    const element = editableRefs.current[id];
    if (!element) return;

    element.focus();
    
    const command = format === 'bold' ? 'bold' : format === 'italic' ? 'italic' : 'underline';
    document.execCommand(command, false);
    
    // Update the state with the new HTML
    handleUpdateItem(id, { text: element.innerHTML });
  };

  const normalizeToHttps = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return trimmed;
    if (/^(https?:\/\/|mailto:|tel:)/i.test(trimmed)) return trimmed.replace(/^http:\/\//i, "https://");
    return `https://${trimmed}`;
  };

  const saveSelectionRange = (id: string) => {
    const selection = window.getSelection();
    const element = editableRefs.current[id];
    if (!selection || selection.rangeCount === 0 || !element) return;

    const range = selection.getRangeAt(0);
    if (!element.contains(range.commonAncestorContainer)) return;
    selectionRanges.current[id] = range.cloneRange();
  };

  const restoreSelectionRange = (id: string) => {
    const selection = window.getSelection();
    const range = selectionRanges.current[id];
    if (!selection || !range) return;
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const openLinkEditor = (id: string) => {
    saveSelectionRange(id);
    setLinkEditorItemId(id);
    const selection = window.getSelection();
    const element = editableRefs.current[id];
    let existingHref = "";

    if (selection && selection.rangeCount > 0 && element) {
      const range = selection.getRangeAt(0);
      const startNode =
        range.startContainer.nodeType === Node.ELEMENT_NODE
          ? (range.startContainer as Element)
          : range.startContainer.parentElement;
      const endNode =
        range.endContainer.nodeType === Node.ELEMENT_NODE
          ? (range.endContainer as Element)
          : range.endContainer.parentElement;

      const startAnchor = startNode?.closest("a");
      const endAnchor = endNode?.closest("a");
      const sameAnchor = startAnchor && startAnchor === endAnchor ? startAnchor : null;
      const anchorInEditor = sameAnchor && element.contains(sameAnchor) ? sameAnchor : null;

      if (anchorInEditor) {
        existingHref = (anchorInEditor as HTMLAnchorElement).getAttribute("href") ?? "";
      }
    }

    setLinkUrl(existingHref);
  };

  const applyLink = (id: string) => {
    const element = editableRefs.current[id];
    if (!element) return;

    const normalizedUrl = normalizeToHttps(linkUrl);
    if (!normalizedUrl) return;

    element.focus();
    restoreSelectionRange(id);
    document.execCommand("createLink", false, normalizedUrl);
    element.querySelectorAll("a").forEach((anchor) => {
      (anchor as HTMLAnchorElement).style.color = "#2563eb";
      (anchor as HTMLAnchorElement).style.textDecoration = "underline";
    });
    handleUpdateItem(id, { text: element.innerHTML });
    setLinkEditorItemId(null);
    setLinkUrl("");
  };

  const getTextLength = (html: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent?.length || 0;
  };

  const saveCursorPosition = (element: HTMLDivElement) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;
    
    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(element);
    preCaretRange.setEnd(range.endContainer, range.endOffset);
    return preCaretRange.toString().length;
  };

  const restoreCursorPosition = (element: HTMLDivElement, position: number) => {
    const selection = window.getSelection();
    if (!selection) return;

    let charCount = 0;
    const nodeStack: Node[] = [element];
    let node: Node | undefined;
    let foundPosition = false;

    while (!foundPosition && (node = nodeStack.pop())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const textNode = node as Text;
        const nextCharCount = charCount + (textNode.length || 0);
        if (position <= nextCharCount) {
          const range = document.createRange();
          range.setStart(textNode, Math.min(position - charCount, textNode.length || 0));
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
          foundPosition = true;
        }
        charCount = nextCharCount;
      } else {
        const children = Array.from(node.childNodes);
        for (let i = children.length - 1; i >= 0; i--) {
          nodeStack.push(children[i]);
        }
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {items.map((item, index) => (
          <div key={item.id} className="space-y-2">
            <div className="flex items-start gap-2">
               <div className="pt-2 w-5 flex-shrink-0 flex flex-row items-center gap-1">
                  <span className="text-sm font-medium text-muted-foreground">
                    {index + 1}.
                  </span>
                  <Checkbox 
                      id={`summary-check-${item.id}`}
                      checked={item.isChecked}
                      onCheckedChange={(checked) => handleUpdateItem(item.id, { isChecked: !!checked })}
                  />
               </div>
               <div className="flex-1">
                   {item.isChecked ? (
                     <div
                       className="min-h-[40px] w-full ml-[12px] rounded-md border border-input bg-background text-sm focus-within:outline-none focus-within:ring-1 focus-within:ring-ring"
                       style={{ paddingLeft: '12px', paddingRight: '12px', paddingTop: '8px', paddingBottom: '8px' }}
                     >
                       <input
                         type="text"
                         value={item.text}
                         onChange={(e) => {
                             const newText = e.target.value;
                             if (newText.length > 200) return; 
                             handleUpdateItem(item.id, { text: newText });
                         }}
                         placeholder="Header (Bold, Italic, Underline)"
                         className="w-full bg-transparent border-0 outline-none font-bold italic underline p-0 m-0"
                         dir="ltr"
                       />
                     </div>
                   ) : (
                     <div className="relative">
                       {!item.text && (
                         <div className="absolute inset-0 text-sm text-muted-foreground pointer-events-none" style={{ paddingLeft: '20px', paddingTop: '8px' }}>
                           Plain text
                         </div>
                       )}
                       <div
                         ref={(el) => { 
                           editableRefs.current[item.id] = el;
                           if (el && item.text && el.innerHTML !== item.text) {
                             el.innerHTML = item.text;
                           }
                         }}
                         contentEditable
                         suppressContentEditableWarning
                         onInput={(e) => {
                           const element = e.currentTarget;
                           const cursorPos = saveCursorPosition(element);
                           const newText = element.innerHTML;
                           const textLength = element.textContent?.length || 0;
                           
                           if (textLength > 115) {
                             element.innerHTML = item.text;
                             if (cursorPos !== null) {
                               restoreCursorPosition(element, cursorPos - 1);
                             }
                             return;
                           }
                           
                           handleUpdateItem(item.id, { text: newText });
                           
                           // Restore cursor after state update
                           setTimeout(() => {
                             if (cursorPos !== null && element === document.activeElement) {
                               restoreCursorPosition(element, cursorPos);
                             }
                           }, 0);
                         }}
                         onKeyDown={(e) => {
                           // Handle keyboard shortcuts
                           if (e.ctrlKey || e.metaKey) {
                             if (e.key === 'b' || e.key === 'B') {
                               e.preventDefault();
                               applyFormat(item.id, 'bold');
                             } else if (e.key === 'i' || e.key === 'I') {
                               e.preventDefault();
                               applyFormat(item.id, 'italic');
                             } else if (e.key === 'u' || e.key === 'U') {
                               e.preventDefault();
                               applyFormat(item.id, 'underline');
                             } else if (e.key === 'k' || e.key === 'K') {
                               e.preventDefault();
                               openLinkEditor(item.id);
                             }
                           }
                         }}
                         onPaste={(e) => {
                           e.preventDefault();
                           
                           // Get plain text from clipboard
                           const text = e.clipboardData.getData('text/plain');
                           if (!text) return;
                           
                           const element = e.currentTarget;
                           const currentLength = element.textContent?.length || 0;
                           
                           // Check if pasting would exceed limit
                           const selection = window.getSelection();
                           if (selection && selection.rangeCount > 0) {
                             const range = selection.getRangeAt(0);
                             const selectedLength = range.toString().length;
                             const newLength = currentLength - selectedLength + text.length;
                             
                             if (newLength > 115) {
                               // Truncate text to fit within limit
                               const availableSpace = 115 - (currentLength - selectedLength);
                               const truncatedText = text.substring(0, availableSpace);
                               
                               if (truncatedText) {
                                 range.deleteContents();
                                 const textNode = document.createTextNode(truncatedText);
                                 range.insertNode(textNode);
                                 
                                 // Move cursor to end of inserted text
                                 range.setStartAfter(textNode);
                                 range.collapse(true);
                                 selection.removeAllRanges();
                                 selection.addRange(range);
                               }
                             } else {
                               // Paste normally
                               range.deleteContents();
                               const textNode = document.createTextNode(text);
                               range.insertNode(textNode);
                               
                               // Move cursor to end of inserted text
                               range.setStartAfter(textNode);
                               range.collapse(true);
                               selection.removeAllRanges();
                               selection.addRange(range);
                             }
                             
                             // Force update the state immediately
                             setTimeout(() => {
                               handleUpdateItem(item.id, { text: element.innerHTML });
                             }, 0);
                           }
                         }}
                         onBlur={(e) => {
                           const element = e.currentTarget;
                           // If empty after blur, ensure state is updated
                           if (!element.textContent?.trim()) {
                             handleUpdateItem(item.id, { text: '' });
                           }
                         }}
                         className="min-h-[40px] w-full ml-[12px] rounded-md border border-input bg-background text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                         style={{ 
                           direction: 'ltr', 
                           textAlign: 'left',
                           minHeight: '60px',
                           maxHeight: '120px',
                           overflowY: 'auto',
                           paddingLeft: '8px',
                           paddingRight: '12px',
                           paddingTop: '8px',
                           paddingBottom: '8px'
                         }}
                         dir="ltr"
                       />
                       <div className="text-xs text-right text-gray-400 mt-1">
                         {getTextLength(item.text)} / 115
                       </div>
                     </div>
                   )}
                   {item.isChecked && (
                       <div className="text-xs text-right text-gray-400 mt-1">
                           {item.text.length} / 200
                       </div>
                   )}
               </div>
               {items.length > 1 && (
                   <Button 
                     type="button" 
                     size="icon" 
                     variant="ghost" 
                     onClick={() => handleDeleteItem(item.id)}
                     className="shrink-0"
                   >
                       <svg 
                         xmlns="http://www.w3.org/2000/svg" 
                         width="20" 
                         height="20" 
                         viewBox="0 0 256 256" 
                         fill="currentColor"
                         className="text-red-500"
                       >
                         <path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"></path>
                       </svg>
                   </Button>
               )}
            </div>
            
            {!item.isChecked && (
              <div className="flex gap-2" style={{ marginLeft: '28px' }}>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => applyFormat(item.id, 'bold')}
                  className="h-8 w-8 p-0"
                  title="Bold (Ctrl+B)"
                >
                  <b>B</b>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => applyFormat(item.id, 'italic')}
                  className="h-8 w-8 p-0"
                  title="Italic (Ctrl+I)"
                >
                  <i>I</i>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => applyFormat(item.id, 'underline')}
                  className="h-8 w-8 p-0"
                  title="Underline (Ctrl+U)"
                >
                  <u>U</u>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => openLinkEditor(item.id)}
                  className="h-8 w-8 p-0"
                  title="Add Link (Ctrl+K)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" viewBox="0 0 256 256"><path d="M165.66,90.34a8,8,0,0,1,0,11.32l-64,64a8,8,0,0,1-11.32-11.32l64-64A8,8,0,0,1,165.66,90.34ZM215.6,40.4a56,56,0,0,0-79.2,0L106.34,70.45a8,8,0,0,0,11.32,11.32l30.06-30a40,40,0,0,1,56.57,56.56l-30.07,30.06a8,8,0,0,0,11.31,11.32L215.6,119.6a56,56,0,0,0,0-79.2ZM138.34,174.22l-30.06,30.06a40,40,0,1,1-56.56-56.57l30.05-30.05a8,8,0,0,0-11.32-11.32L40.4,136.4a56,56,0,0,0,79.2,79.2l30.06-30.07a8,8,0,0,0-11.32-11.31Z"></path></svg>
                </Button>
              </div>
            )}
            {!item.isChecked && linkEditorItemId === item.id && (
              <div className="flex gap-2" style={{ marginLeft: '28px' }}>
                <Input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  onBlur={(e) => setLinkUrl(normalizeToHttps(e.target.value))}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applyLink(item.id);
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      setLinkEditorItemId(null);
                      setLinkUrl("");
                    }
                  }}
                  placeholder="https://example.com"
                  className="h-8"
                  autoFocus
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => applyLink(item.id)}
                  className="h-8"
                >
                  Apply
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setLinkEditorItemId(null);
                    setLinkUrl("");
                  }}
                  className="h-8"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <Button type="button" variant="outline" onClick={handleAddItem} className="w-full">
        <i className="ph ph-bold ph-plus mr-2" />
        Add More
      </Button>
      
      <div className="text-xs text-gray-500 mt-2">
        <p>Checked items will be formatted as <b><i><u>Header (Bold + Italic + Underline)</u></i></b>.</p>
        <p>Unchecked items: Select text and click B/I/U/Link buttons to format. Ctrl+K opens link input.</p>
      </div>
    </div>
  );
};
